import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridReadyEvent, GridApi, CellEditingStoppedEvent } from 'ag-grid-enterprise';
import {
  FileText, FileSpreadsheet, Image, FlaskConical, Dna, FileIcon,
  Presentation, BookOpen, Microscope, Upload, Download,
  FilePlus, Pencil, Shield, Archive, Table2, PackageCheck,
  List, LayoutGrid, Clock, Sparkles, FolderPlus, ChevronLeft, ChevronRight,
  Folder, FolderOpen,
} from 'lucide-react';
import { documentsApi, Document } from '../../api/documents';
import { chemistryApi } from '../../api/chemistry';
import { ChemFile } from '../../types/chemistry';
import { useUIStore } from '../../stores/uiStore';
import { getStoredToken } from '../../utils/tokenStorage';
import FileDataCounts from '../common/FileDataCounts';
import { DATA_POINT_DESCRIPTION, formatDataCount, withFileDataSummary } from '../../utils/fileDataSummary';


// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const CATEGORY_COLORS: Record<string, string> = {
  chemistry: '#0D9488',
  biology: '#2563EB',
  literature: '#6B7280',
  presentation: '#8B5CF6',
  report: '#4B5563',
  image: '#EA580C',
  structure: '#2563EB',
  data: '#F59E0B',
  other: '#9CA3AF',
};

const CATEGORY_LABELS: Record<string, string> = {
  chemistry: 'Chemistry',
  biology: 'Biology',
  literature: 'Literature',
  presentation: 'Presentations',
  report: 'Reports',
  image: 'Images',
  structure: 'Structures',
  data: 'Data',
};

type ViewMode = 'list' | 'grid' | 'table';
type FolderType = 'recents' | 'whatsnew' | 'all' | 'category' | 'custom';

interface FolderItem {
  type: FolderType;
  id: string;       // 'recents', 'all', or category name, or custom folder name
  label: string;
  count: number;
  color?: string;
  newCount?: number; // What's New badge count
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function categoryIcon(category: string, size = 16) {
  const props = { size, strokeWidth: 1.5 };
  switch (category) {
    case 'chemistry': return <FlaskConical {...props} />;
    case 'biology': return <Dna {...props} />;
    case 'literature': return <BookOpen {...props} />;
    case 'presentation': return <Presentation {...props} />;
    case 'report': return <FileText {...props} />;
    case 'image': return <Image {...props} />;
    case 'structure': return <Microscope {...props} />;
    case 'data': return <FileSpreadsheet {...props} />;
    default: return <FileIcon {...props} />;
  }
}

function relativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function cleanFileName(name: string) {
  return name.replace(/^[a-f0-9]{8}_/i, '');
}

// localStorage keys for recents and what's new
function recentsKey(projectId: number) {
  return `discoverant_recents_${projectId}`;
}
function lastVisitKey(projectId: number, folderId: string) {
  return `discoverant_lastvisit_${projectId}_${folderId}`;
}

function getRecentDocIds(projectId: number): number[] {
  try {
    const raw = localStorage.getItem(recentsKey(projectId));
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function addRecentDocId(projectId: number, docId: number) {
  const ids = getRecentDocIds(projectId).filter(id => id !== docId);
  ids.unshift(docId);
  localStorage.setItem(recentsKey(projectId), JSON.stringify(ids.slice(0, 10)));
}

function getLastVisit(projectId: number, folderId: string): number {
  try {
    const key = lastVisitKey(projectId, folderId);
    const stored = localStorage.getItem(key);
    if (stored) return parseInt(stored, 10);

    // A first-time visitor has no meaningful "since your last visit" baseline.
    // Seed it now so historical project files are not all presented as unread.
    const baseline = Date.now();
    localStorage.setItem(key, String(baseline));
    return baseline;
  } catch { return Date.now(); }
}

function setLastVisit(projectId: number, folderId: string) {
  localStorage.setItem(lastVisitKey(projectId, folderId), String(Date.now()));
}

// Per-file read tracking
function readFilesKey(projectId: number) {
  return `discoverant_files_read_${projectId}`;
}

function getReadFileIds(projectId: number): Record<number, number> {
  try {
    return JSON.parse(localStorage.getItem(readFilesKey(projectId)) || '{}');
  } catch { return {}; }
}

function markDocRead(projectId: number, docId: number) {
  const read = getReadFileIds(projectId);
  read[docId] = Date.now();
  localStorage.setItem(readFilesKey(projectId), JSON.stringify(read));
}

/** Extract custom folder names from document tags */
function getCustomFolders(docs: Document[]): string[] {
  const folders = new Set<string>();
  for (const doc of docs) {
    for (const tag of (doc.tags || [])) {
      if (tag.startsWith('folder:')) {
        folders.add(tag.slice(7));
      }
    }
  }
  return Array.from(folders).sort();
}

/** Check if doc belongs to a custom folder */
function isInCustomFolder(doc: Document, folderName: string): boolean {
  return (doc.tags || []).includes(`folder:${folderName}`);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ProjectFilesTabProps {
  projectId: number;
  onDocumentSelect: (doc: Document) => void;
  onNavigateToEditor?: (fileId: string) => void;
  /** External folder navigation request (from DocumentDetailPanel folder pills) */
  navigateToFolder?: string | null;
}

const ProjectFilesTab: React.FC<ProjectFilesTabProps> = ({ projectId, onDocumentSelect, onNavigateToEditor, navigateToFolder }) => {
  const showNotification = useUIStore((s) => s.showNotification);

  // Core state
  const [allDocuments, setAllDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const gridApiRef = useRef<GridApi | null>(null);

  // Chemistry file mapping for "Open in Grid"
  const [dataFileMap, setDataFileMap] = useState<Map<string, string>>(new Map());

  // View state
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [selectedFolder, setSelectedFolder] = useState<string>('all');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  // Custom folder creation
  const [showNewFolderModal, setShowNewFolderModal] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [folderTick, setFolderTick] = useState(0);

  // Per-file read tracking for unread highlighting
  const [readFileIds, setReadFileIds] = useState<Record<number, number>>(() => getReadFileIds(projectId));

  // Context menu
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; doc: Document } | null>(null);

  // Fetch ALL documents + chemistry files for accurate sidebar counts
  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const [docData, chemFiles] = await Promise.all([
        documentsApi.listDocuments(projectId, {}),
        chemistryApi.getFiles(projectId).catch(() => [] as ChemFile[]),
      ]);

      // Deduplicate: track doc filenames so we don't double-count chem files
      const docFileNames = new Set(docData.map(d => d.original_filename?.toLowerCase()));

      // Convert chemistry files to Document shape (same pattern as GlobalFilesPage)
      const chemDocs: Document[] = chemFiles
        .filter(f => !docFileNames.has((f.originalFileName || f.fileName).toLowerCase()))
        .map(f => {
          const category = f.fileType === 'reactions' ? 'chemistry'
            : f.fileType === 'biologics' ? 'biology'
            : 'chemistry';
          return {
            id: -parseInt(f.id, 10),
            tenant_id: 0,
            project_id: projectId,
            uploaded_by: f.uploadedBy || '',
            original_filename: f.originalFileName || f.fileName,
            display_name: f.originalFileName || f.fileName,
            description: null,
            mime_type: null,
            category,
            tags: [],
            current_version: 1,
            processing_status: f.status === 'completed' ? 'COMPLETE' : f.status?.toUpperCase() || 'COMPLETE',
            sharing_policy: 'private',
            is_archived: false,
            created_at: f.createdDate ? new Date(f.createdDate).toISOString() : new Date().toISOString(),
            updated_at: f.modifiedDate ? new Date(f.modifiedDate).toISOString() : (f.createdDate ? new Date(f.createdDate).toISOString() : new Date().toISOString()),
            _chemFileId: f.id,
            compoundCount: f.compoundCount,
            biologicalDataPointCount: f.biologicalDataPointCount,
            biologicalEntityCount: f.biologicalEntityCount,
            dataSummaryStatus: f.dataSummaryStatus,
          } as Document & { _chemFileId?: string };
        });

      setAllDocuments([...docData.map(d => withFileDataSummary(d, chemFiles)), ...chemDocs]);
    } catch (err: any) {
      showNotification('error', err?.response?.data?.detail || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [projectId, showNotification]);

  const fetchDataFileMap = useCallback(async () => {
    try {
      const files = await chemistryApi.getFiles(projectId);
      const map = new Map<string, string>();
      for (const f of files) {
        const name = cleanFileName(f.originalFileName || f.fileName).toLowerCase();
        map.set(name, f.id);
      }
      setDataFileMap(map);
    } catch {
      // Silently fail
    }
  }, [projectId]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments]);
  useEffect(() => { fetchDataFileMap(); }, [fetchDataFileMap]);

  // Record last visit when LEAVING a folder (cleanup), not when entering
  useEffect(() => {
    return () => {
      setLastVisit(projectId, selectedFolder);
    };
  }, [projectId, selectedFolder]);

  // Respond to external folder navigation
  useEffect(() => {
    if (navigateToFolder) {
      setSelectedFolder(navigateToFolder);
      setSidebarCollapsed(false);
    }
  }, [navigateToFolder]);

  // Get data file ID for a doc
  const getDataFileId = useCallback((doc: Document): string | null => {
    return dataFileMap.get(doc.original_filename.toLowerCase()) || null;
  }, [dataFileMap]);

  // ---------------------------------------------------------------------------
  // Sidebar folder items
  // ---------------------------------------------------------------------------

  const recentDocIds = useMemo(() => getRecentDocIds(projectId), [projectId, allDocuments]);

  const recentDocs = useMemo(() => {
    return recentDocIds
      .map(id => allDocuments.find(d => d.id === id))
      .filter((d): d is Document => d != null)
      .slice(0, 5);
  }, [recentDocIds, allDocuments]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const doc of allDocuments) {
      const cat = doc.category?.toLowerCase();
      if (cat) counts[cat] = (counts[cat] || 0) + 1;
    }
    return counts;
  }, [allDocuments]);

  const customFolders = useMemo(() => getCustomFolders(allDocuments), [allDocuments]);

  const customFolderCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const folder of customFolders) {
      counts[folder] = allDocuments.filter(d => isInCustomFolder(d, folder)).length;
    }
    return counts;
  }, [allDocuments, customFolders]);

  // What's New: count unread files (uploaded after last visit AND not clicked)
  const newCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    // For "whatsnew" folder — total unread across all docs
    const whatsNewLv = getLastVisit(projectId, 'whatsnew');
    counts['whatsnew'] = allDocuments.filter(d => new Date(d.created_at).getTime() > whatsNewLv && !readFileIds[d.id]).length;
    // For "all" folder
    const allLv = getLastVisit(projectId, 'all');
    counts['all'] = allDocuments.filter(d => new Date(d.created_at).getTime() > allLv && !readFileIds[d.id]).length;
    // For category folders
    for (const cat of Object.keys(categoryCounts)) {
      const lv = getLastVisit(projectId, cat);
      counts[cat] = allDocuments.filter(d => d.category?.toLowerCase() === cat && new Date(d.created_at).getTime() > lv && !readFileIds[d.id]).length;
    }
    // For custom folders
    for (const folder of customFolders) {
      const lv = getLastVisit(projectId, `custom:${folder}`);
      counts[`custom:${folder}`] = allDocuments.filter(d => isInCustomFolder(d, folder) && new Date(d.created_at).getTime() > lv && !readFileIds[d.id]).length;
    }
    return counts;
  }, [allDocuments, projectId, categoryCounts, customFolders, readFileIds]);

  // Build folder list
  const folderItems = useMemo((): FolderItem[] => {
    const items: FolderItem[] = [];

    // Recents
    items.push({ type: 'recents', id: 'recents', label: 'Recents', count: recentDocs.length });

    // What's New
    items.push({
      type: 'whatsnew', id: 'whatsnew', label: "What's New", count: newCounts['whatsnew'] || 0,
      newCount: selectedFolder !== 'whatsnew' ? (newCounts['whatsnew'] || 0) : 0,
    });

    // All Files
    items.push({
      type: 'all', id: 'all', label: 'All Files', count: allDocuments.length,
      newCount: selectedFolder !== 'all' ? newCounts['all'] : 0,
    });

    // Smart folders (categories with > 0 files)
    for (const [cat, count] of Object.entries(categoryCounts).sort((a, b) => b[1] - a[1])) {
      if (count > 0 && CATEGORY_LABELS[cat]) {
        items.push({
          type: 'category', id: cat, label: CATEGORY_LABELS[cat], count,
          color: CATEGORY_COLORS[cat],
          newCount: selectedFolder !== cat ? newCounts[cat] : 0,
        });
      }
    }

    return items;
  }, [recentDocs, allDocuments, categoryCounts, selectedFolder, newCounts]);

  // ---------------------------------------------------------------------------
  // Filtered documents based on selected folder + search
  // ---------------------------------------------------------------------------

  const filteredDocuments = useMemo(() => {
    let docs = allDocuments;

    // Folder filter
    if (selectedFolder === 'recents') {
      docs = recentDocs;
    } else if (selectedFolder === 'whatsnew') {
      const whatsNewLv = getLastVisit(projectId, 'whatsnew');
      docs = docs.filter(d => new Date(d.created_at).getTime() > whatsNewLv && !readFileIds[d.id]);
    } else if (selectedFolder === 'all') {
      // No filter
    } else if (selectedFolder.startsWith('custom:')) {
      const folderName = selectedFolder.slice(7);
      docs = docs.filter(d => isInCustomFolder(d, folderName));
    } else {
      // Category folder
      docs = docs.filter(d => d.category?.toLowerCase() === selectedFolder);
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      docs = docs.filter(d =>
        d.display_name.toLowerCase().includes(q) ||
        d.original_filename.toLowerCase().includes(q) ||
        (d.tags || []).some(t => t.toLowerCase().includes(q))
      );
    }

    return docs;
  }, [allDocuments, selectedFolder, searchQuery, recentDocs, projectId, readFileIds]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleSearchChange = useCallback((value: string) => {
    setSearchQuery(value);
  }, []);

  const handleFolderSelect = useCallback((folderId: string) => {
    setSelectedFolder(folderId);
  }, []);

  const handleDocClick = useCallback((doc: Document) => {
    addRecentDocId(projectId, doc.id);
    markDocRead(projectId, doc.id);
    setReadFileIds({ ...getReadFileIds(projectId) });
    onDocumentSelect(doc);
  }, [projectId, onDocumentSelect]);

  /** Is this doc unread? (created after last folder visit AND not yet clicked) */
  const isDocUnread = useCallback((doc: Document): boolean => {
    const folderKey = selectedFolder;
    const lastVisitTs = getLastVisit(projectId, folderKey);
    const docTime = new Date(doc.created_at).getTime();
    if (docTime <= lastVisitTs) return false;
    if (readFileIds[doc.id]) return false;
    return true;
  }, [projectId, selectedFolder, readFileIds]);

  // Upload
  const handleUpload = useCallback(async (files: FileList | File[]) => {
    setUploading(true);
    setUploadProgress(0);
    const fileArray = Array.from(files);
    try {
      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        const uploaded = await documentsApi.uploadDocument(projectId, file, undefined, (p) => {
          setUploadProgress(Math.round(((i + p / 100) / fileArray.length) * 100));
        });
        const catLabel = CATEGORY_LABELS[uploaded.category] || uploaded.category;
        showNotification('success', `${file.name} uploaded → ${catLabel}`);
      }
      await fetchDocuments();
    } catch (err: any) {
      showNotification('error', err?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [projectId, fetchDocuments, showNotification]);

  // Drag and drop
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files.length > 0) {
      handleUpload(e.dataTransfer.files);
    }
  }, [handleUpload]);

  // Context menu actions
  const handleDownload = useCallback(async (doc: Document & { _chemFileId?: string }) => {
    const token = getStoredToken();

    // Chemistry files (negative id) use the files download endpoint
    if (doc.id < 0 && doc._chemFileId) {
      try {
        const blob = await chemistryApi.downloadFile(doc._chemFileId);
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = doc.original_filename;
        a.click();
        URL.revokeObjectURL(blobUrl);
      } catch {
        showNotification('error', 'Download failed');
      }
      return;
    }

    // Real documents use the documents endpoint
    const url = documentsApi.downloadUrl(doc.id);
    const a = document.createElement('a');
    a.download = doc.original_filename;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then(blob => {
        const blobUrl = URL.createObjectURL(blob);
        a.href = blobUrl;
        a.click();
        URL.revokeObjectURL(blobUrl);
      })
      .catch(() => showNotification('error', 'Download failed'));
  }, [showNotification]);

  const handleArchive = useCallback(async (doc: Document) => {
    if (!window.confirm(`Archive "${doc.display_name}"?`)) return;
    try {
      await documentsApi.archiveDocument(doc.id);
      showNotification('success', `${doc.display_name} archived`);
      await fetchDocuments();
    } catch (err: any) {
      showNotification('error', 'Archive failed');
    }
  }, [fetchDocuments, showNotification]);

  const handleNewVersion = useCallback((doc: Document) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        await documentsApi.uploadVersion(doc.id, file);
        showNotification('success', `New version uploaded for ${doc.display_name}`);
        await fetchDocuments();
      } catch (err: any) {
        showNotification('error', 'Version upload failed');
      }
    };
    input.click();
  }, [fetchDocuments, showNotification]);

  const handleRename = useCallback(async (doc: Document, newName: string) => {
    if (!newName.trim() || newName === doc.display_name) return;
    try {
      await documentsApi.updateDocument(doc.id, { display_name: newName.trim() });
      await fetchDocuments();
    } catch (err: any) {
      showNotification('error', 'Rename failed');
    }
  }, [fetchDocuments, showNotification]);

  // Custom folder creation
  const handleCreateFolder = useCallback(() => {
    const name = newFolderName.trim();
    if (!name) return;
    // Just close the modal — the folder appears when files are tagged
    setShowNewFolderModal(false);
    setNewFolderName('');
    showNotification('success', `Folder "${name}" created. Drag files to add them.`);
    // Set the folder in state so it appears even when empty
    setSelectedFolder(`custom:${name}`);
    // Store empty folder marker
    const key = `discoverant_customfolders_${projectId}`;
    try {
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      if (!existing.includes(name)) {
        existing.push(name);
        localStorage.setItem(key, JSON.stringify(existing));
      }
    } catch {}
    setFolderTick(t => t + 1);
  }, [newFolderName, projectId, showNotification]);

  // Get persisted custom folder names (includes empty folders)
  const persistedCustomFolders = useMemo(() => {
    try {
      const key = `discoverant_customfolders_${projectId}`;
      return JSON.parse(localStorage.getItem(key) || '[]') as string[];
    } catch { return []; }
  }, [projectId, allDocuments, folderTick]);

  const allCustomFolders = useMemo(() => {
    const set = new Set([...customFolders, ...persistedCustomFolders]);
    return Array.from(set).sort();
  }, [customFolders, persistedCustomFolders]);

  // Add file to custom folder via tag
  const addToFolder = useCallback(async (doc: Document, folderName: string) => {
    const tag = `folder:${folderName}`;
    if ((doc.tags || []).includes(tag)) return;
    try {
      await documentsApi.updateDocument(doc.id, { tags: [...(doc.tags || []), tag] });
      await fetchDocuments();
      showNotification('success', `Added to "${folderName}"`);
    } catch {
      showNotification('error', 'Failed to add to folder');
    }
  }, [fetchDocuments, showNotification]);

  // Close context menu on click elsewhere
  useEffect(() => {
    const handler = () => setContextMenu(null);
    if (contextMenu) document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [contextMenu]);

  // ---------------------------------------------------------------------------
  // AG Grid column definitions (for Table view)
  // ---------------------------------------------------------------------------

  const columnDefs = useMemo(() => [
    {
      headerName: '',
      field: 'category',
      width: 48,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        if (!params.data) return null;
        const color = CATEGORY_COLORS[params.data.category] || '#9CA3AF';
        return (
          <span style={{ color, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            {categoryIcon(params.data.category, 18)}
          </span>
        );
      },
    },
    {
      headerName: 'Name',
      field: 'display_name',
      flex: 2,
      minWidth: 200,
      editable: true,
      cellStyle: { fontWeight: 500 },
    },
    {
      headerName: 'Category',
      field: 'category',
      width: 120,
      cellRenderer: (params: any) => {
        if (!params.value) return null;
        const color = CATEGORY_COLORS[params.value] || '#9CA3AF';
        return (
          <span
            style={{
              display: 'inline-block',
              padding: '2px 8px',
              borderRadius: 9999,
              fontSize: 11,
              fontWeight: 600,
              color: '#fff',
              backgroundColor: color,
              textTransform: 'capitalize',
            }}
          >
            {params.value}
          </span>
        );
      },
    },
    {
      field: 'compoundCount', headerName: 'Compounds', width: 120,
      type: 'numericColumn', sortable: true, filter: 'agNumberColumnFilter',
      valueFormatter: (params: any) => formatDataCount(params.value),
    },
    {
      field: 'biologicalDataPointCount', headerName: 'Bio data points', width: 150,
      type: 'numericColumn', sortable: true, filter: 'agNumberColumnFilter',
      headerTooltip: DATA_POINT_DESCRIPTION,
      valueFormatter: (params: any) => formatDataCount(params.value),
    },
    {
      headerName: 'Version',
      field: 'current_version',
      width: 80,
      valueGetter: (params: any) => params.data ? `v${params.data.current_version}` : '',
      cellStyle: { fontFamily: 'JetBrains Mono, monospace', fontSize: 12 },
    },
    {
      headerName: 'Status',
      field: 'processing_status',
      width: 100,
      cellRenderer: (params: any) => {
        if (!params.value) return null;
        const color = params.value === 'COMPLETE' ? '#10B981' : params.value === 'ERROR' ? '#EF4444' : '#F59E0B';
        return (
          <span style={{ color, fontSize: 11, fontWeight: 600 }}>{params.value}</span>
        );
      },
    },
    {
      headerName: 'Tags',
      field: 'tags',
      flex: 1,
      minWidth: 120,
      cellRenderer: (params: any) => {
        const tags: string[] = (params.value || []).filter((t: string) => !t.startsWith('folder:'));
        if (tags.length === 0) return <span style={{ color: 'var(--text-muted)' }}>-</span>;
        return (
          <span style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            {tags.slice(0, 3).map((t: string) => (
              <span
                key={t}
                style={{
                  padding: '1px 6px',
                  borderRadius: 4,
                  fontSize: 10,
                  fontWeight: 500,
                  backgroundColor: 'var(--hover-bg)',
                  color: 'var(--text-secondary)',
                }}
              >
                {t}
              </span>
            ))}
            {tags.length > 3 && (
              <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>+{tags.length - 3}</span>
            )}
          </span>
        );
      },
    },
    {
      headerName: 'Modified',
      field: 'updated_at',
      width: 120,
      valueFormatter: (params: any) => params.value ? relativeTime(params.value) : '',
      cellStyle: { color: 'var(--text-muted)', fontSize: 12 },
    },
  ] as ColDef[], []);

  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: true,
    resizable: true,
  }), []);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    gridApiRef.current = event.api;
  }, []);

  const onCellEditingStopped = useCallback((event: CellEditingStoppedEvent) => {
    if (event.column.getColId() === 'display_name' && event.data) {
      handleRename(event.data, event.newValue);
    }
  }, [handleRename]);

  const onRowClicked = useCallback((event: any) => {
    if (event.data) handleDocClick(event.data);
  }, [handleDocClick]);

  const onCellContextMenu = useCallback((event: any) => {
    if (event.event && event.data) {
      event.event.preventDefault();
      setContextMenu({ x: event.event.clientX, y: event.event.clientY, doc: event.data });
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Render: Sidebar
  // ---------------------------------------------------------------------------

  const renderSidebar = () => (
    <div
      className="flex-shrink-0 border-r flex flex-col overflow-hidden transition-all duration-200"
      style={{
        width: sidebarCollapsed ? 0 : 240,
        minWidth: sidebarCollapsed ? 0 : 240,
        borderColor: 'var(--card-border, #e5e7eb)',
      }}
    >
      {!sidebarCollapsed && (
        <div className="flex flex-col h-full overflow-y-auto py-2">
          {/* Recents */}
          <SidebarItem
            icon={<Clock size={15} strokeWidth={1.5} />}
            label="Recents"
            count={recentDocs.length}
            selected={selectedFolder === 'recents'}
            onClick={() => handleFolderSelect('recents')}
          />

          {/* What's New */}
          <SidebarItem
            icon={<Sparkles size={15} strokeWidth={1.5} />}
            label="What's New"
            count={newCounts['whatsnew'] || 0}
            selected={selectedFolder === 'whatsnew'}
            onClick={() => handleFolderSelect('whatsnew')}
            newCount={selectedFolder !== 'whatsnew' ? (newCounts['whatsnew'] || 0) : 0}
          />

          <div className="mx-3 my-1.5 border-t" style={{ borderColor: 'var(--card-border, #e5e7eb)' }} />

          {/* All Files */}
          <SidebarItem
            icon={<Folder size={15} strokeWidth={1.5} />}
            label="All Files"
            count={allDocuments.length}
            selected={selectedFolder === 'all'}
            onClick={() => handleFolderSelect('all')}
            newCount={selectedFolder !== 'all' ? (newCounts['all'] || 0) : 0}
          />

          <div className="mx-3 my-1.5 border-t" style={{ borderColor: 'var(--card-border, #e5e7eb)' }} />

          {/* Smart Folders header */}
          <div className="px-3 py-1.5">
            <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted, #9ca3af)' }}>
              Smart Folders
            </span>
          </div>

          {folderItems.filter(f => f.type === 'category').map(folder => (
            <SidebarItem
              key={folder.id}
              icon={categoryIcon(folder.id, 15)}
              label={folder.label}
              count={folder.count}
              selected={selectedFolder === folder.id}
              onClick={() => handleFolderSelect(folder.id)}
              dotColor={folder.color}
              newCount={folder.newCount}
            />
          ))}

          <div className="mx-3 my-1.5 border-t" style={{ borderColor: 'var(--card-border, #e5e7eb)' }} />

          {/* My Folders header */}
          <div className="px-3 py-1.5">
            <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-muted, #9ca3af)' }}>
              My Folders
            </span>
          </div>

          {allCustomFolders.map(folder => (
            <SidebarItem
              key={`custom:${folder}`}
              icon={selectedFolder === `custom:${folder}` ? <FolderOpen size={15} strokeWidth={1.5} /> : <Folder size={15} strokeWidth={1.5} />}
              label={folder}
              count={customFolderCounts[folder] || 0}
              selected={selectedFolder === `custom:${folder}`}
              onClick={() => handleFolderSelect(`custom:${folder}`)}
              newCount={newCounts[`custom:${folder}`] || 0}
            />
          ))}

          {/* + New Folder */}
          <button
            onClick={() => setShowNewFolderModal(true)}
            className="mx-2 mt-1 px-3 py-1.5 flex items-center gap-2 text-xs font-medium rounded-md transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
            style={{ color: 'var(--text-muted, #9ca3af)' }}
          >
            <FolderPlus size={14} strokeWidth={1.5} />
            <span>New Folder</span>
          </button>

          {/* Spacer */}
          <div className="flex-1" />
        </div>
      )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render: Recents bar (horizontal strip at top of main area)
  // ---------------------------------------------------------------------------

  const renderRecentsBar = () => {
    if (recentDocs.length === 0) return null;
    return (
      <div className="flex items-center gap-2 px-1 pb-3 overflow-x-auto" style={{ minHeight: 36 }}>
        <span className="text-[10px] font-bold uppercase tracking-widest flex-shrink-0" style={{ color: 'var(--text-muted, #9ca3af)' }}>
          Recent
        </span>
        {recentDocs.slice(0, 5).map(doc => (
          <button
            key={doc.id}
            onClick={() => handleDocClick(doc)}
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium flex-shrink-0 transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
            style={{
              backgroundColor: 'var(--hover-bg, #f3f4f6)',
              color: 'var(--text-secondary, #6b7280)',
              border: '1px solid var(--card-border, #e5e7eb)',
              borderRadius: 6,
            }}
            title={`${doc.display_name} — Opened ${relativeTime(doc.updated_at)}`}
          >
            <span style={{ color: CATEGORY_COLORS[doc.category] || '#9CA3AF' }}>
              {categoryIcon(doc.category, 12)}
            </span>
            <span className="truncate" style={{ maxWidth: 120 }}>{doc.display_name}</span>
          </button>
        ))}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: List View
  // ---------------------------------------------------------------------------

  const renderListFileRow = (doc: Document) => {
    const unread = isDocUnread(doc);
    return (
      <div
        key={doc.id}
        onClick={() => handleDocClick(doc)}
        onContextMenu={(e) => {
          e.preventDefault();
          setContextMenu({ x: e.clientX, y: e.clientY, doc });
        }}
        className="flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 border-b"
        style={{
          borderColor: 'var(--card-border, #e5e7eb)',
          minHeight: 40,
          borderLeft: unread ? '3px solid #0D9488' : '3px solid transparent',
        }}
      >
        {/* Unread dot */}
        {unread && (
          <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-teal-500" />
        )}

        {/* File icon */}
        <span style={{ color: CATEGORY_COLORS[doc.category] || '#9CA3AF', flexShrink: 0 }}>
          {categoryIcon(doc.category, 16)}
        </span>

        {/* Filename — bold if unread */}
        <span
          className="flex-1 text-sm truncate"
          style={{
            color: 'var(--text-primary, #111827)',
            fontWeight: unread ? 600 : 400,
          }}
        >
          {doc.display_name}
        </span>

        <FileDataCounts data={doc} />

        {/* Category pill */}
        <span
          style={{
            display: 'inline-block',
            padding: '1px 8px',
            borderRadius: 9999,
            fontSize: 10,
            fontWeight: 600,
            color: '#fff',
            backgroundColor: CATEGORY_COLORS[doc.category] || '#9CA3AF',
            textTransform: 'capitalize',
            flexShrink: 0,
          }}
        >
          {doc.category}
        </span>

        {/* Version */}
        <span
          className="flex-shrink-0"
          style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: 'var(--text-muted, #9ca3af)' }}
        >
          v{doc.current_version}
        </span>

        {/* Date */}
        <span
          className="flex-shrink-0 text-right"
          style={{ fontSize: 11, color: 'var(--text-muted, #9ca3af)', width: 70 }}
        >
          {relativeTime(doc.updated_at)}
        </span>
      </div>
    );
  };

  const renderListView = () => {
    // Split into new/earlier for section headers
    const newDocs = filteredDocuments.filter(d => isDocUnread(d));
    const earlierDocs = filteredDocuments.filter(d => !isDocUnread(d));

    return (
      <div className="flex-1 overflow-y-auto">
        {/* New files section */}
        {newDocs.length > 0 && (
          <>
            <div className="px-3 py-1.5 text-xs font-semibold text-teal-600 dark:text-teal-400 bg-teal-50/50 dark:bg-teal-900/10 border-b border-teal-100 dark:border-teal-900/20">
              {newDocs.length} new file{newDocs.length !== 1 ? 's' : ''}
            </div>
            {newDocs.map(doc => renderListFileRow(doc))}
            {earlierDocs.length > 0 && (
              <div className="px-3 py-1.5 text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider border-b border-gray-100 dark:border-gray-800">
                Earlier
              </div>
            )}
          </>
        )}
        {earlierDocs.map(doc => renderListFileRow(doc))}
        {filteredDocuments.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="w-14 h-14 mb-3 bg-gradient-to-br from-emerald-100 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/20 rounded-full flex items-center justify-center">
              <FileText className="w-7 h-7 text-emerald-400" />
            </div>
            <p className="text-sm font-medium" style={{ color: 'var(--text-secondary, #6b7280)' }}>No files in this folder</p>
            <p className="text-xs mt-1" style={{ color: 'var(--text-muted, #9ca3af)' }}>
              {searchQuery ? 'Try a different search term' : 'Upload files or select a different folder'}
            </p>
          </div>
        )}
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // Render: Grid View
  // ---------------------------------------------------------------------------

  const renderGridView = () => (
    <div
      className="flex-1 overflow-y-auto p-2"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 12,
        alignContent: 'start',
      }}
    >
      {filteredDocuments.map(doc => {
        const unread = isDocUnread(doc);
        return (
          <div
            key={doc.id}
            onClick={() => handleDocClick(doc)}
            onContextMenu={(e) => {
              e.preventDefault();
              setContextMenu({ x: e.clientX, y: e.clientY, doc });
            }}
            className="flex flex-col items-center p-4 rounded-lg border cursor-pointer transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
            style={{
              backgroundColor: 'var(--card-bg, #fff)',
              borderColor: unread ? '#0D9488' : 'var(--card-border, #e5e7eb)',
              borderWidth: unread ? 2 : 1,
              borderRadius: 8,
            }}
          >
            {/* Large icon */}
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center mb-3 relative"
              style={{
                backgroundColor: `${CATEGORY_COLORS[doc.category] || '#9CA3AF'}15`,
              }}
            >
              <span style={{ color: CATEGORY_COLORS[doc.category] || '#9CA3AF' }}>
                {categoryIcon(doc.category, 24)}
              </span>
              {unread && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-teal-500 border-2 border-white dark:border-gray-900" />
              )}
            </div>

            {/* Filename — bold if unread */}
            <span
              className="text-sm text-center truncate w-full"
              style={{
                color: 'var(--text-primary, #111827)',
                fontWeight: unread ? 600 : 500,
              }}
              title={doc.display_name}
            >
              {doc.display_name}
            </span>

            <div className="mt-2 text-center"><FileDataCounts data={doc} /></div>

            {/* Category + version row */}
            <div className="flex items-center gap-2 mt-2">
              <span
                style={{
                  display: 'inline-block',
                  padding: '1px 6px',
                  borderRadius: 9999,
                  fontSize: 9,
                  fontWeight: 600,
                  color: '#fff',
                  backgroundColor: CATEGORY_COLORS[doc.category] || '#9CA3AF',
                  textTransform: 'capitalize',
                }}
              >
                {doc.category}
              </span>
              <span style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 10, color: 'var(--text-muted, #9ca3af)' }}>
                v{doc.current_version}
              </span>
            </div>

            {/* Date */}
            <span className="text-[10px] mt-1.5" style={{ color: 'var(--text-muted, #9ca3af)' }}>
              {relativeTime(doc.updated_at)}
            </span>
          </div>
        );
      })}
      {filteredDocuments.length === 0 && !loading && (
        <div className="col-span-full flex flex-col items-center justify-center py-16">
          <div className="w-14 h-14 mb-3 bg-gradient-to-br from-emerald-100 to-teal-50 dark:from-emerald-900/30 dark:to-teal-900/20 rounded-full flex items-center justify-center">
            <FileText className="w-7 h-7 text-emerald-400" />
          </div>
          <p className="text-sm font-medium" style={{ color: 'var(--text-secondary, #6b7280)' }}>No files in this folder</p>
        </div>
      )}
    </div>
  );

  // ---------------------------------------------------------------------------
  // Render: Table View (existing AG Grid)
  // ---------------------------------------------------------------------------

  const renderTableView = () => (
    <div className="flex-1 min-h-0 ag-theme-alpine" style={{ width: '100%' }}>
      <AgGridReact
        rowData={filteredDocuments}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        onGridReady={onGridReady}
        onCellEditingStopped={onCellEditingStopped}
        onRowClicked={onRowClicked}
        onCellContextMenu={onCellContextMenu}
        rowSelection="single"
        animateRows
        getRowId={(params) => String(params.data.id)}
        rowHeight={40}
        headerHeight={36}
        suppressContextMenu
        domLayout="normal"
      />
    </div>
  );

  // ---------------------------------------------------------------------------
  // Main Render
  // ---------------------------------------------------------------------------

  return (
    <div className="flex flex-col h-full">
      {/* Upload Zone */}
      <div
        data-document-drop-zone="true"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className="cursor-pointer transition-all mb-1.5 flex-shrink-0"
        style={{
          border: isDragging ? '2px dashed #0D9488' : '2px dashed var(--card-border, #e5e7eb)',
          backgroundColor: isDragging ? 'rgba(13,148,136,0.05)' : 'transparent',
          borderRadius: 8,
          padding: uploading ? '6px 16px' : '6px 16px',
          textAlign: 'center',
        }}
      >
        {uploading ? (
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-sm" style={{ color: 'var(--text-muted, #9ca3af)' }}>Uploading... {uploadProgress}%</span>
            <div className="flex-1 h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
              <div className="h-full bg-emerald-500 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2">
            <Upload size={16} style={{ color: 'var(--text-muted, #9ca3af)' }} />
            <span className="text-sm" style={{ color: 'var(--text-muted, #9ca3af)' }}>Drop files here or click to upload</span>
          </div>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv,.txt,.md,.rtf,.odt,.ods,.odp,.png,.jpg,.jpeg,.tif,.tiff,.svg"
          className="hidden"
          onChange={(e) => e.target.files && handleUpload(e.target.files)}
        />
      </div>

      {/* Main layout: sidebar + content */}
      <div className="flex flex-1 min-h-0 overflow-hidden rounded-lg border" style={{ borderColor: 'var(--card-border, #e5e7eb)' }}>
        {/* Sidebar */}
        {renderSidebar()}

        {/* Sidebar toggle */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="flex-shrink-0 flex items-center justify-center w-4 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          style={{ borderRight: '1px solid var(--card-border, #e5e7eb)' }}
          title={sidebarCollapsed ? 'Show sidebar' : 'Hide sidebar'}
        >
          {sidebarCollapsed ? <ChevronRight size={10} /> : <ChevronLeft size={10} />}
        </button>

        {/* Main content area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Recents bar */}
          <div className="px-3 pt-1.5">
            {renderRecentsBar()}
          </div>

          {/* Filter bar + view toggle */}
          <div className="flex items-center gap-3 px-3 pb-1 flex-shrink-0">
            {/* Search */}
            <div className="relative flex-1 max-w-xs">
              <input
                type="text"
                placeholder="Search files..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500 transition-all"
                style={{
                  backgroundColor: 'var(--hover-bg, #f9fafb)',
                  border: '1px solid var(--card-border, #e5e7eb)',
                  color: 'var(--text-primary, #111827)',
                }}
              />
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--text-muted, #9ca3af)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* File count */}
            <span className="text-xs ml-auto" style={{ color: 'var(--text-muted, #9ca3af)' }}>
              {filteredDocuments.length} file{filteredDocuments.length !== 1 ? 's' : ''}
            </span>

            {/* Export Audit Report */}
            <button
              onClick={() => {
                const token = getStoredToken();
                fetch(`/api/v1/projects/${projectId}/evidence-pack`, {
                  headers: { Authorization: `Bearer ${token}` },
                })
                  .then(r => {
                    if (!r.ok) throw new Error(`HTTP ${r.status}`);
                    return r.blob();
                  })
                  .then(blob => {
                    const a = document.createElement('a');
                    a.href = URL.createObjectURL(blob);
                    a.download = `audit-report-project-${projectId}.zip`;
                    a.click();
                    URL.revokeObjectURL(a.href);
                  })
                  .catch(() => {});
              }}
              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md border transition-colors hover:bg-gray-50 dark:hover:bg-gray-800"
              style={{ borderColor: 'var(--card-border, #e5e7eb)', color: 'var(--text-secondary, #6b7280)' }}
              title="Download audit report for all project documents"
            >
              <PackageCheck size={13} />
              <span>Export Audit Report</span>
            </button>

            {/* View mode toggle */}
            <div
              className="flex items-center rounded-md border overflow-hidden"
              style={{ borderColor: 'var(--card-border, #e5e7eb)' }}
            >
              {([
                { mode: 'list' as ViewMode, icon: <List size={14} />, title: 'List view' },
                { mode: 'grid' as ViewMode, icon: <LayoutGrid size={14} />, title: 'Grid view' },
                { mode: 'table' as ViewMode, icon: <Table2 size={14} />, title: 'Table view' },
              ]).map(({ mode, icon, title }) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className="px-2 py-1.5 transition-colors"
                  style={{
                    backgroundColor: viewMode === mode ? '#0D9488' : 'transparent',
                    color: viewMode === mode ? '#fff' : 'var(--text-muted, #9ca3af)',
                  }}
                  title={title}
                >
                  {icon}
                </button>
              ))}
            </div>
          </div>

          {/* Content area */}
          {loading ? (
            <div className="flex flex-col items-center justify-center flex-1 py-16">
              <div className="w-8 h-8 border-3 border-emerald-500 border-t-transparent rounded-full animate-spin mb-3" />
              <span className="text-sm" style={{ color: 'var(--text-muted, #9ca3af)' }}>Loading files...</span>
            </div>
          ) : (
            <>
              {viewMode === 'list' && renderListView()}
              {viewMode === 'grid' && renderGridView()}
              {viewMode === 'table' && renderTableView()}
            </>
          )}
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed z-50 rounded-lg shadow-2xl border py-1.5 min-w-[180px]"
          style={{
            left: contextMenu.x,
            top: contextMenu.y,
            borderColor: 'var(--card-border, #e5e7eb)',
            backgroundColor: 'var(--card-bg, #fff)',
          }}
        >
          {onNavigateToEditor && getDataFileId(contextMenu.doc) && (
            <button
              onClick={() => {
                const fid = getDataFileId(contextMenu.doc);
                if (fid) onNavigateToEditor(fid);
                setContextMenu(null);
              }}
              className="w-full px-4 py-2 text-left text-sm text-emerald-600 hover:bg-emerald-50 dark:hover:bg-gray-800 flex items-center gap-2 font-medium"
            >
              <Table2 size={14} /> Open in Grid
            </button>
          )}
          <button
            onClick={() => { handleDownload(contextMenu.doc); setContextMenu(null); }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
            style={{ color: 'var(--text-primary, #374151)' }}
          >
            <Download size={14} /> Download
          </button>
          <button
            onClick={() => { handleNewVersion(contextMenu.doc); setContextMenu(null); }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
            style={{ color: 'var(--text-primary, #374151)' }}
          >
            <FilePlus size={14} /> New Version
          </button>
          <button
            onClick={() => {
              const name = window.prompt('New name:', contextMenu.doc.display_name);
              if (name) handleRename(contextMenu.doc, name);
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
            style={{ color: 'var(--text-primary, #374151)' }}
          >
            <Pencil size={14} /> Rename
          </button>
          <button
            onClick={() => {
              documentsApi.getDocument(contextMenu.doc.id).then(detail => {
                const hash = detail.versions?.[0]?.sha256_hash || '';
                navigator.clipboard.writeText(hash);
                showNotification('success', 'SHA-256 copied');
              });
              setContextMenu(null);
            }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
            style={{ color: 'var(--text-primary, #374151)' }}
          >
            <Shield size={14} /> Copy SHA-256
          </button>
          <button
            onClick={() => { handleDocClick(contextMenu.doc); setContextMenu(null); }}
            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
            style={{ color: 'var(--text-primary, #374151)' }}
          >
            <FileText size={14} /> View Audit Trail
          </button>

          {/* Add to folder submenu */}
          {allCustomFolders.length > 0 && (
            <>
              <div className="my-1 border-t" style={{ borderColor: 'var(--card-border, #e5e7eb)' }} />
              {allCustomFolders.filter(f => !isInCustomFolder(contextMenu.doc, f)).map(folder => (
                <button
                  key={folder}
                  onClick={() => { addToFolder(contextMenu.doc, folder); setContextMenu(null); }}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center gap-2"
                  style={{ color: 'var(--text-primary, #374151)' }}
                >
                  <FolderPlus size={14} /> Add to {folder}
                </button>
              ))}
            </>
          )}

          <div className="my-1 border-t" style={{ borderColor: 'var(--card-border, #e5e7eb)' }} />
          <button
            onClick={() => { handleArchive(contextMenu.doc); setContextMenu(null); }}
            className="w-full px-4 py-2 text-left text-sm text-red-500 hover:bg-red-50 dark:hover:bg-gray-800 flex items-center gap-2"
          >
            <Archive size={14} /> Archive
          </button>
        </div>
      )}

      {/* New Folder Modal */}
      {showNewFolderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div
            className="rounded-xl shadow-2xl p-6 w-80"
            style={{ backgroundColor: 'var(--card-bg, #fff)' }}
          >
            <h3 className="text-sm font-semibold mb-3" style={{ color: 'var(--text-primary, #111827)' }}>
              New Folder
            </h3>
            <input
              type="text"
              placeholder="Folder name..."
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleCreateFolder()}
              autoFocus
              className="w-full px-3 py-2 text-sm rounded-md mb-4 focus:outline-none focus:ring-2 focus:ring-emerald-500"
              style={{
                border: '1px solid var(--card-border, #e5e7eb)',
                backgroundColor: 'var(--hover-bg, #f9fafb)',
                color: 'var(--text-primary, #111827)',
              }}
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => { setShowNewFolderModal(false); setNewFolderName(''); }}
                className="px-3 py-1.5 text-sm rounded-md transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                style={{ color: 'var(--text-secondary, #6b7280)' }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateFolder}
                disabled={!newFolderName.trim()}
                className="px-3 py-1.5 text-sm font-medium text-white rounded-md transition-colors disabled:opacity-40"
                style={{ backgroundColor: '#0D9488' }}
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SidebarItem sub-component
// ---------------------------------------------------------------------------

const SidebarItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  count: number;
  selected: boolean;
  onClick: () => void;
  dotColor?: string;
  newCount?: number;
}> = ({ icon, label, count, selected, onClick, dotColor, newCount }) => (
  <button
    onClick={onClick}
    className="w-full px-3 py-1.5 flex items-center gap-2 text-left transition-colors rounded-md mx-1"
    style={{
      backgroundColor: selected ? 'rgba(13,148,136,0.08)' : 'transparent',
      color: selected ? '#0D9488' : 'var(--text-secondary, #6b7280)',
      width: 'calc(100% - 8px)',
      borderLeft: selected ? '2px solid #0D9488' : '2px solid transparent',
    }}
  >
    {/* Dot or icon */}
    {dotColor && !selected ? (
      <span
        className="flex-shrink-0 rounded-full"
        style={{ width: 7, height: 7, backgroundColor: dotColor }}
      />
    ) : (
      <span className="flex-shrink-0" style={{ color: selected ? '#0D9488' : dotColor || 'var(--text-muted, #9ca3af)' }}>
        {icon}
      </span>
    )}

    {/* Label */}
    <span className="flex-1 text-xs font-medium truncate">{label}</span>

    {/* What's New badge */}
    {(newCount || 0) > 0 && (
      <span
        className="flex-shrink-0 flex items-center justify-center text-white font-bold rounded-full"
        style={{
          backgroundColor: '#EF4444',
          fontSize: 9,
          minWidth: 16,
          height: 16,
          padding: '0 4px',
        }}
      >
        {newCount}
      </span>
    )}

    {/* Count */}
    <span
      className="flex-shrink-0 text-[10px] font-medium"
      style={{
        color: selected ? '#0D9488' : 'var(--text-muted, #9ca3af)',
        fontFamily: 'JetBrains Mono, monospace',
      }}
    >
      {count}
    </span>
  </button>
);

export default ProjectFilesTab;
